<template>
    <div id="retention-page">
        <div class="container-fluid">
            <div class="row">
                <div class="col-md-12 col-sm-12">
                    <loader v-if="is('loading')"></loader>
                    <h1>{{ t("retentionReport-title") }}</h1>
                    <div>
                        <filter-bar
                            @changed="updateFilters"
                            :has-branch-filter="canFilterPerBranch"
                            ref="filterBar"
                            :filters="extraFilters"
                        />
                        <div class="clearfix"></div>
                    </div>
                    <template v-if="is('loaded')">
                        <div class="row stats top-stats">
                            <div class="col-sm-12 col-md-3">
                                <div class="box">
                                    <div class="icon">
                                        <Icon icon-class="ui-coins-stacked-03" size="13" />
                                    </div>
                                    <div class="info">
                                        <div class="number">
                                            {{
                                                formatCurrency(
                                                    counters.averageTicket,
                                                )
                                            }}
                                        </div>
                                        <div class="label">{{
                                            t(
                                                "retentionReport-stats-average_ticket",
                                            )
                                        }}</div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-sm-12 col-md-3">
                                <div class="box">
                                    <div class="icon">
                                        <Icon icon-class="ui-calendar" size="13" />
                                    </div>
                                    <div class="info">
                                        <div class="number">
                                            {{
                                                formatNumber(
                                                    counters.averageLifeTime,
                                                )
                                            }}
                                            {{
                                                t(
                                                    "retentionReport-stats-average_lifetime_suffix",
                                                )
                                            }}
                                        </div>
                                        <div class="label">
                                            {{
                                                t(
                                                    "retentionReport-stats-average_lifetime",
                                                )
                                            }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-sm-12 col-md-3">
                                <div class="box">
                                    <div class="icon">
                                        <Icon icon-class="ui-shopping-cart-03" size="13" />
                                    </div>
                                    <div class="info">
                                        <div class="number">
                                            {{
                                                formatNumber(
                                                    counters.averageOrdersPerLife,
                                                )
                                            }}
                                            {{
                                                t(
                                                    "retentionReport-stats-average_orders_suffix",
                                                )
                                            }}
                                        </div>
                                        <div class="label">
                                            {{
                                                t(
                                                    "retentionReport-stats-average_orders_per_client",
                                                )
                                            }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-sm-12 col-md-3">
                                <div class="box">
                                    <div class="icon">
                                        <Icon icon-class="ui-bank-note-01" size="13" />
                                    </div>
                                    <div class="info">
                                        <div class="number">
                                            {{ formatCurrency(counters.ltv) }}
                                        </div>
                                        <div class="label">{{
                                            t("retentionReport-stats-projected_ltv")
                                        }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <cohort-chart
                            :series="cohort.series"
                            :allowed-intervals="cohort.allowed_intervals"
                            :interval="cohort.interval"
                            :cohort-type="cohort.cohort_type"
                            :segments="segments"
                            :action="cohort.action"
                            :extra="cohort.props"
                            @refresh="updateInterval"
                            @change-active-cell="loadClients"
                        />

                        <div class="box mt-3" v-if="clientsFilter">
                            <h2>{{ t("retentionReport-clients-title") }}</h2>
                            <div class="info">
                                {{ t("retentionReport-clients-cohort") }}:
                                <span v-if="cohort.interval === 'yearly'">
                                    <human-date
                                        format="[YY]"
                                        :date="activeCell.row.start"
                                    />
                                </span>
                                <span v-else-if="cohort.interval === 'monthly'">
                                    <human-date
                                        format="[MM]/[YY]"
                                        :date="activeCell.row.start"
                                    />
                                </span>
                                <span v-else>
                                    <human-date
                                        format="[d]/[MM]/[YY]"
                                        :date="activeCell.row.start"
                                    />
                                </span>
                            </div>
                            <div class="info">
                                {{ t("retentionReport-clients-action") }}:
                                <span v-if="cohort.cohort_type === 'period'">
                                    <span v-if="cohort.action === 'order'">
                                        {{
                                            t(
                                                "retentionReport-clients-action_order",
                                            )
                                        }}
                                    </span>
                                    <span
                                        v-if="
                                            cohort.action === 'joined-segment'
                                        "
                                    >
                                        {{
                                            t(
                                                "retentionReport-clients-action_joined_segment",
                                            )
                                        }}
                                        "{{ segmentName }}"
                                    </span>
                                    <span
                                        v-if="cohort.action === 'left-segment'"
                                    >
                                        {{
                                            t(
                                                "retentionReport-clients-action_left_segment",
                                            )
                                        }}
                                        "{{ segmentName }}"
                                    </span>
                                    <span v-if="cohort.interval === 'yearly'">
                                        {{ t("retentionReport-common-in") }}
                                        <human-date
                                            format="[YY]"
                                            :date="activeCell.column.start"
                                        />
                                    </span>
                                    <span
                                        v-else-if="
                                            cohort.interval === 'monthly'
                                        "
                                    >
                                        {{ t("retentionReport-common-in") }}
                                        <human-date
                                            format="[MM]/[YY]"
                                            :date="activeCell.column.start"
                                        />
                                    </span>
                                    <span v-else>
                                        {{
                                            t("retentionReport-common-between")
                                        }}
                                        <human-date
                                            format="[d]/[MM]/[YY]"
                                            :date="activeCell.column.start"
                                        />
                                        {{ t("retentionReport-common-and") }}
                                        <human-date
                                            format="[d]/[MM]/[YY]"
                                            :date="activeCell.column.end"
                                        />
                                    </span>
                                </span>
                                <span v-if="cohort.cohort_type === 'orders'">
                                    {{
                                        t(
                                            "retentionReport-clients-action_order",
                                        )
                                    }}
                                    {{ activeCell.column.orders }}
                                    {{
                                        activeCell.column.orders !== 1
                                            ? t(
                                                  "retentionReport-clients-times_plural",
                                              )
                                            : t(
                                                  "retentionReport-clients-times_singular",
                                              )
                                    }}
                                    {{
                                        t(
                                            "retentionReport-clients-times_or_more",
                                        )
                                    }}
                                </span>
                            </div>
                            <grid
                                source="clients"
                                :filters="clientsFilter"
                            >
                                <template v-slot:rows="grid">
                                    <thead>
                                        <tr>
                                            <grid-th
                                                :grid="grid"
                                                :sortable="true"
                                                attr="name"
                                                >{{
                                                    t(
                                                        "retentionReport-grid-name",
                                                    )
                                                }}</grid-th
                                            >
                                            <grid-th
                                                :grid="grid"
                                                :sortable="true"
                                                attr="whatsapp"
                                                >{{
                                                    t(
                                                        "retentionReport-grid-phone",
                                                    )
                                                }}</grid-th
                                            >
                                            <grid-th
                                                :grid="grid"
                                                :sortable="true"
                                                attr="birthdate"
                                                class="col-center"
                                                >{{
                                                    t(
                                                        "retentionReport-grid-birthday",
                                                    )
                                                }}</grid-th
                                            >
                                            <grid-th
                                                :grid="grid"
                                                :sortable="true"
                                                attr="average_value"
                                                class="col-center"
                                                >{{
                                                    t(
                                                        "retentionReport-grid-average_ticket",
                                                    )
                                                }}</grid-th
                                            >
                                            <grid-th
                                                :grid="grid"
                                                :sortable="true"
                                                attr="total_value"
                                                class="col-center"
                                                >{{
                                                    t(
                                                        "retentionReport-grid-total_spent",
                                                    )
                                                }}</grid-th
                                            >

                                            <grid-th
                                                :grid="grid"
                                                :sortable="true"
                                                attr="total_orders"
                                                class="col-center"
                                                >{{
                                                    t(
                                                        "retentionReport-grid-total_orders",
                                                    )
                                                }}</grid-th
                                            >
                                            <grid-th
                                                :grid="grid"
                                                :sortable="true"
                                                attr="email"
                                                >{{
                                                    t(
                                                        "retentionReport-grid-email",
                                                    )
                                                }}</grid-th
                                            >
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr
                                            style="cursor: pointer"
                                            v-for="client in grid.data"
                                            :key="client.clients_id"
                                            @click="
                                                $router.push(
                                                    'client/' +
                                                        client.clients_id,
                                                )
                                            "
                                        >
                                            <td style="min-width: 250px">
                                                {{ client.first_name }}
                                                {{ client.last_name }}
                                            </td>
                                            <td style="min-width: 160px">
                                                <btn-whatsapp
                                                    :number="client.whatsapp"
                                                ></btn-whatsapp>
                                                {{ client.whatsapp }}
                                            </td>
                                            <td
                                                style="min-width: 160px"
                                                class="col-center"
                                            >
                                                <template
                                                    v-if="client.birthdate"
                                                >
                                                    <human-date
                                                        :date="client.birthdate"
                                                        format="[d]/[M]/[Y]"
                                                    />
                                                    ({{
                                                        calculateAge(
                                                            client.birthdate,
                                                        )
                                                    }}
                                                    {{
                                                        t(
                                                            "retentionReport-clients-years_old_suffix",
                                                        )
                                                    }})
                                                </template>
                                                <span v-else
                                                    >{{
                                                        t(
                                                            "retentionReport-clients-not_informed",
                                                        )
                                                    }}</span
                                                >
                                            </td>
                                            <td
                                                style="min-width: 170px"
                                                class="col-center"
                                            >
                                                {{ formatCurrency(client.average_value) }}
                                            </td>
                                            <td
                                                style="min-width: 130px"
                                                class="col-center"
                                            >
                                                {{ formatCurrency(client.total_value) }}
                                            </td>

                                            <td
                                                style="min-width: 160px"
                                                class="col-center"
                                            >
                                                {{ client.total_orders }}
                                            </td>

                                            <td style="min-width: 220px">
                                                {{ client.email }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </template>
                                <template v-slot:list="grid">
                                    <div
                                        class="item"
                                        v-for="client in grid.data"
                                        :key="client.clients_id"
                                    >
                                        <span class="info title">
                                            {{ client.first_name }}
                                            {{ client.last_name }}
                                        </span>
                                        <span class="info">
                                            <span class="pull-left"
                                                >{{ client.whatsapp }}
                                            </span>
                                            <btn-whatsapp
                                                :number="client.whatsapp"
                                            ></btn-whatsapp>
                                        </span>
                                        <div class="clear"></div>
                                        <span class="info">
                                            {{ client.email }}
                                        </span>
                                        <button
                                            class="btn btn-sm btn-primary"
                                            @click="
                                                $router.push(
                                                    'client/' +
                                                        client.clients_id,
                                                )
                                            "
                                        >
                                            {{
                                                t(
                                                    "retentionReport-clients-details_button",
                                                )
                                            }}
                                        </button>
                                    </div>
                                </template>
                            </grid>
                            <div style="clear: both"></div>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import keviApi from "../keviApi.js";
import cohortChart from "../components/charts/cohortChart";
import HumanDate from "../components/human-date/human-date";
import Icon from "../components/icon/icon";
import currencyMixin from "../mixins/currency-mixin";
import { placeStartOfDayISO, placeEndOfDayISO } from "../helpers/datetime";
export default {
    mixins: [currencyMixin],
    components: {
        HumanDate,
        cohortChart,
        Icon
    },
    data: function () {
        return {
            state: "loading",
            activeCell: {
                row: {},
                column: {},
            },
            cohort: {
                interval: "",
                allowed_intervals: [],
                series: [],
                props: {},
                action: "",
                cohort_type: "period",
            },
            clientsFilter: null,
            counters: {
                averageLifeTime: 0,
                averageOrdersPerLife: 0,
                averageTicket: 0,
                ltv: 0,
            },
            filters: {},
            segments: [],
            extraFilters: [],
        };
    },
    mounted: function () {
        document.getElementsByTagName("title")[0].innerText =
            this.t("retentionReport-page_title");

        const filterBar = this.$refs.filterBar;
        filterBar.addFilter("period");
        if (Object.keys(this.$route.query).length === 0) {
          const tz =
            (this.$store.state.place && this.$store.state.place.timezone) ||
            undefined;
          const now = new Date();
          const sixMonthsAgo = new Date(now);
          sixMonthsAgo.setMonth(now.getMonth() - 6);
          sixMonthsAgo.setDate(1);
          const lastDayOfPreviousMonth = new Date(now.getFullYear(), now.getMonth(), 0);
          // Emit place-tz day boundaries so the value round-trips through the
          // period filter without the day shifting when browser/place zones differ.
          const initialFilters = {
            start: placeStartOfDayISO(sixMonthsAgo, tz),
            end: placeEndOfDayISO(lastDayOfPreviousMonth, tz)
          };
          filterBar.setValue("period", initialFilters);

        }
        if (this.$module("segments")) {
            let options = keviApi.getOptions("segments");
            this.$http(options).then((response) => {
                let segments = response.body;
                this.segments = segments;
                this.extraFilters.push({
                    component: "checkbox",
                    label: this.t(
                        "retentionReport-filters-client_segment",
                    ),
                    name: "client-segments",
                    options: {
                        options: segments.map((seg) => ({
                            value: seg.id,
                            label: seg.name,
                        })),
                        icon: "fa-tag",
                    },
                    removable: true,
                });
            });
        }
        window.setTimeout(() => {
          this.ready = true;
          this.loadData();
        }, 100);
    },
    computed: {
        place() {
            return this.$store.state.place;
        },
        user() {
            return this.$store.state.user;
        },
        canFilterPerBranch() {
            return !!this.user.permissions["clients"]["options"][
                "global_access"
            ];
        },
        segmentName() {
            if (
                this.cohort.action === "joined-segment" ||
                this.cohort.action === "left-segment"
            ) {
                let segment = this.segments.find(
                    (s) => s.id === this.cohort.props.segment_id,
                );
                if (segment) {
                    return segment.name;
                }
            }
            return "";
        },
    },
    methods: {
        t: function (term, replaces) {
            return this.$root.t(term, replaces);
        },
        is: function (state) {
            return this.state === state;
        },
        formatNumber(number) {
            return parseFloat(number).toLocaleString("pt-BR");
        },
        setState: function (state) {
            this.state = state;
        },
        loadData: function (settings) {
            this.state = "loading";
            let data = {
                filters: this.filters,
            };

            if (!settings) {
              settings = this.cohort;
            }
            if (settings && settings.interval) {
                data.interval = settings.interval;
            }

            if (settings && settings.action) {
                data.action = settings.action;
            }

            if (settings && settings.props) {
                data.props = settings.props;
            }

            if (settings && settings.cohortType) {
                data.cohortType = settings.cohortType;
            }

            let requestOptions = keviApi.getOptions(
                "clients/retention-report",
                "GET",
                data,
            );
            this.$http(requestOptions).then((response) => {
                if (response.status === 200) {
                    if (response.body) {
                        this.cohort = response.body.cohort;
                        this.clientsFilter = null;
                        this.state = "loaded";
                    } else {
                        this.state = "empty";
                    }
                } else {
                    this.state = "error";
                }
            });

            let requestStatsOptions = keviApi.getOptions(
              "clients/retention-report-stats",
              "GET",
              data,
            );
            this.$http(requestStatsOptions).then((response) => {
              if (response.status === 200) {
                if (response.body) {
                  this.counters = response.body.stats;
                  this.clientsFilter = null;
                  this.state = "loaded";
                } else {
                  this.state = "empty";
                }
              } else {
                this.state = "error";
              }
            });
        },
        loadClients(options) {
            this.activeCell = options;
            let filters = {
                "period-first-order": {
                    start: options.row.start,
                    end: options.row.end,
                },
            };
            if (this.cohort.cohort_type === "period") {
                if (this.cohort.action === "joined-segment") {
                    filters["joined-segment-period"] = {
                        "segment-id": this.cohort.props.segment_id,
                        start: options.column.start,
                        end: options.column.end,
                    };
                }
                if (this.cohort.action === "left-segment") {
                    filters["left-segment-period"] = {
                        "segment-id": this.cohort.props.segment_id,
                        start: options.column.start,
                        end: options.column.end,
                    };
                }
                if (this.cohort.action === "order") {
                    filters["period"] = {
                        start: options.column.start,
                        end: options.column.end,
                    };
                }
            }
            if (this.cohort.cohort_type === "orders") {
                filters["amount-orders"] = {
                    min: options.column.orders,
                };
            }

            if (this.filters["client-segments"]) {
                filters["client-segments"] = this.filters["client-segments"];
            }
            this.clientsFilter = filters;
        },
        updateInterval: function (settings) {
            this.loadData(settings);
        },
        updateFilters: function (filters) {
            this.filters = filters;
            if (!this.ready) {
              return;
            }
            this.loadData();
        },
        calculateAge: function (birthday) {
            birthday = new Date(birthday);
            let ageDifMs = Date.now() - birthday.getTime();
            let ageDate = new Date(ageDifMs); // miliseconds from epoch
            return Math.abs(ageDate.getUTCFullYear() - 1970);
        },
    },
};
</script>
<style>
.page-content.retention-report {
    max-width: 100%;
}
</style>
